import React, {FC, useMemo} from 'react';

import {
  MatchSimple,
  MatchResult,
  Probabilities,
  TeamInfo,
  Sport,
  TeamRankings,
} from '../../../utils/types';

import Ad from '../../Common/Ad';
import MatchListItem from '../../Common/MatchListItem';

import './styles.scss';
import BarChart from '../../Common/BarChart';

const matchPlaceholders = new Array(5).fill('').map((p, i) => i.toString());

interface FormaMatchProps {
  match?: MatchResult;
}

const FormaMatch: FC<FormaMatchProps> = ({match}: FormaMatchProps) => {
  let result = null;
  let info = null;

  if (match) {
    let type;

    switch (match.result) {
      case 'N':
        type = 'win';
        break;
      case 'H':
        type = 'loss';
        break;
      case 'I':
        type = 'tie';
        break;
      default:
    }

    result = (
      <span className={`Forma__result ${type ? `Forma__result--${type}` : ''}`}>
        {match.result}
      </span>
    );

    info = (
      <span className="Forma__info">
        <span>{match.teams[0].shortname}</span>
        <span>
          {match.score[0]}:{match.score[1]}
        </span>
      </span>
    );
  } else {
    result = <span className="Forma__result Forma__result--empty" />;
  }

  return (
    <div className="Forma__match">
      {result}
      {info}
    </div>
  );
};

interface Props {
  sport: Sport;
  teams: {
    home: TeamInfo;
    away: TeamInfo;
  };
  teamRankings?: TeamRankings;
  probabilities?: Probabilities;
  formaMatches?: MatchSimple[];
  formaHome?: MatchResult[];
  formaAway?: MatchResult[];
}

const WidgetPreview: FC<Props> = ({
  sport,
  teams,
  teamRankings,
  probabilities,
  formaMatches,
  formaHome,
  formaAway,
}: Props) => {
  const tieProbability = useMemo(
    () =>
      [Sport.SOCCER, Sport.HANDBALL].includes(sport) &&
      probabilities &&
      probabilities.home &&
      probabilities.away &&
      (100 - probabilities.home.probability - probabilities.away.probability).toFixed(0),
    [probabilities]
  );

  return (
    <>
      <section className="WidgetPreview">
        {teamRankings && (
          <>
            <h2 className="WidgetPreview__header">ΚΑΤΑΤΑΞΗ</h2>
            <div className="WidgetPreview__widget">
              <div className="Rankings">
                <div className="Rankings__teams">
                  <div className="Rankings__team Rankings__team--home">
                    <h3 className="Rankings__teamName">{teamRankings.home.name}</h3>
                    <img
                      className="Rankings__flag"
                      src={teamRankings.home.flag}
                      alt={teamRankings.home.country}
                    />
                    <p className="Rankings__country">{teamRankings.home.country}</p>
                  </div>
                  <div className="Rankings__team Rankings__team--away">
                    <h3 className="Rankings__teamName">{teamRankings.away.name}</h3>
                    <img
                      className="Rankings__flag"
                      src={teamRankings.away.flag}
                      alt={teamRankings.away.country}
                    />
                    <p className="Rankings__country">{teamRankings.away.country}</p>
                  </div>
                </div>
                <div className="Rankings__bar">
                  <div className="Rankings__rank Rankings__rank--home">
                    {teamRankings.home.rank}
                  </div>
                  <div className="Rankings__association">{`ΒΑΘΜΟΛΟΓΙΑ ${teamRankings.association}`}</div>
                  <div className="Rankings__rank Rankings__rank--away">
                    {teamRankings.away.rank}
                  </div>
                </div>
                <div className="Rankings__labels">
                  <div className="Rankings__points Rankings__points--home">
                    {teamRankings.home.points}
                  </div>
                  <div className="Rankings__label">ΒΑΘΜΟΙ</div>
                  <div className="Rankings__points Rankings__points--away">
                    {teamRankings.away.points}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {probabilities && probabilities.home && probabilities.away && (
          <>
            <h2 className="WidgetPreview__header">ΠΙΘΑΝΟΤΗΤΕΣ</h2>
            <div className="WidgetPreview__widget">
              <BarChart
                home={{name: probabilities.home.name, value: `${probabilities.home.probability}`}}
                away={{name: probabilities.away.name, value: `${probabilities.away.probability}`}}
                middle={tieProbability ? {name: 'ΙΣΟΠΑΛΙΑ', value: tieProbability} : undefined}
                footer="POWERED BY SPORTRADAR"
              />
            </div>
          </>
        )}
        {((formaMatches && !!formaMatches.length) ||
          (formaHome && !!formaHome.length) ||
          (formaAway && !!formaAway.length)) && <h2 className="WidgetPreview__header">ΦΟΡΜΑ</h2>}
        {formaMatches && !!formaMatches.length && (
          <>
            <h3 className="WidgetPreview__subtitle">ΠΡΟΪΣΤΟΡΙΑ</h3>
            {formaMatches.slice(0, 5).map(match => (
              <MatchListItem
                match={match}
                className="WidgetPreview__match"
                key={match.matchId}
                hasDate
              />
            ))}
          </>
        )}
        {(formaHome && !!formaHome.length) ||
          (formaAway && !!formaAway.length && (
            <>
              <h3 className="WidgetPreview__subtitle">ΤΡΕΧΟΥΣΑ ΦΟΡΜΑ</h3>
              <div className="WidgetPreview__widget Forma">
                {formaHome && !!formaHome.length && (
                  <>
                    <h3 className="Forma__teamName">{teams.home.name}</h3>
                    <div className="Forma__team Forma__team--home">
                      {matchPlaceholders
                        .map(i => `home-${i}`)
                        .map((k, i) => (
                          <FormaMatch match={formaHome[i]} key={k} />
                        ))}
                    </div>
                  </>
                )}
                {formaAway && !!formaAway.length && (
                  <>
                    <h3 className="Forma__teamName">{teams.away.name}</h3>
                    <div className="Forma__team Forma__team--away">
                      {matchPlaceholders
                        .map(i => `away-${i}`)
                        .map((k, i) => (
                          <FormaMatch match={formaAway[i]} key={k} />
                        ))}
                    </div>
                  </>
                )}
              </div>
            </>
          ))}
      </section>
    </>
  );
};

export default React.memo(WidgetPreview);
